<template>
  <section class="AZ-slider-page">
    <div class="container">
      <div class="row">
        <div class="col-lg-12">
          <div class="slider-box">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">در حال بارگذاری...</span>
              </div>
            </div>

            <!-- Slider -->
            <div v-else-if="banners.length > 0" ref="sliderEl" class="swiper sliderSwiper banner-fade-slider">
              <div class="swiper-wrapper">
                <div 
                  v-for="banner in banners" 
                  :key="banner.id" 
                  class="swiper-slide"
                  @click="handleBannerClick(banner)"
                >
                  <component 
                    :is="banner.link_url ? 'a' : 'div'"
                    :href="banner.link_url || undefined"
                    :target="banner.link_url ? '_blank' : undefined"
                    :rel="banner.link_url ? 'noopener noreferrer' : undefined"
                    class="slider-img AZ-img-container"
                  >
                    <div class="AZ-img-container-inner AZ-img-cover">
                      <img 
                        v-if="!hasImageFallback(banner)"
                        :src="getBannerImage(banner)" 
                        :alt="getBannerAlt(banner)" 
                        @error="handleImageError($event, banner)"
                      />
                      <div v-else class="banner-placeholder">
                        <span class="banner-placeholder__badge">بنر پیش‌فرض</span>
                        <h3>{{ getBannerTitle(banner) || fallbackTitle }}</h3>
                        <p>{{ getBannerDescription(banner) || fallbackDescription }}</p>
                        <span class="banner-placeholder__note">جای تصویر نهایی</span>
                      </div>
                    </div>
                  </component>
                </div>
              </div>
              <template v-if="banners.length > 1">
                <div ref="prevEl" class="swiper-button-prev sliderSwiper__prev">
                  <span class="icon-arrow-right-circle"></span>
                </div>
                <div ref="paginationEl" class="swiper-pagination sliderSwiper__pagination"></div>
                <div ref="nextEl" class="swiper-button-next sliderSwiper__next">
                  <span class="icon-arrow-left-circle"></span>
                </div>
              </template>
            </div>

            <!-- Fallback - No Banners -->
            <div v-else-if="showFallback" ref="sliderEl" class="swiper sliderSwiper banner-fade-slider">
              <div class="swiper-wrapper">
                <div class="swiper-slide">
                  <div class="slider-img slider-img--placeholder AZ-img-container">
                    <div class="banner-placeholder">
                      <span class="banner-placeholder__badge">بنر پیش‌فرض</span>
                      <h3>{{ fallbackTitle }}</h3>
                      <p>{{ fallbackDescription }}</p>
                      <span class="banner-placeholder__note">جای تصویر نهایی</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { bannerAPI } from '@/api'

export default {
  name: 'BannerSlider',
  props: {
    // نوع بنر برای دریافت از API
    type: {
      type: String,
      default: 'main_slider'
    },
    // نمایش fallback در صورت عدم وجود بنر
    showFallback: {
      type: Boolean,
      default: true
    },
    // تصویر fallback
    fallbackImage: {
      type: String,
      default: '/assets/img/slider.png'
    },
    // عنوان fallback
    fallbackTitle: {
      type: String,
      default: 'دریافت تسهیلات'
    },
    // توضیحات fallback
    fallbackDescription: {
      type: String,
      default: '500 میلیون تومان وام بگیر با امتیاز'
    },
    showTextOverlay: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      banners: [],
      loading: false,
      error: null,
      swiperInstance: null,
      failedBannerImageIds: []
    }
  },
  mounted() {
    this.fetchBanners()
  },
  beforeUnmount() {
    this.destroySwiper()
  },
  methods: {
    async fetchBanners() {
      this.loading = true
      this.error = null
      
      try {
        const response = await bannerAPI.getByType(this.type)
        if (response.data && response.data.data) {
          this.banners = response.data.data
          
          // Track views for all banners
          this.banners.forEach(banner => {
            this.trackView(banner.id)
          })
        }
      } catch (error) {
        console.error('Error fetching banners:', error)
        this.error = error
      } finally {
        this.loading = false
        this.$nextTick(() => {
          this.initSwiper()
        })
      }
    },
    initSwiper() {
      const sliderEl = this.$refs.sliderEl

      if (!window.Swiper || !sliderEl) {
        return
      }

      this.destroySwiper()

      const hasMultipleSlides = this.banners.length > 1

      this.swiperInstance = new window.Swiper(sliderEl, {
        loop: hasMultipleSlides,
        slidesPerView: 1,
        spaceBetween: 0,
        effect: hasMultipleSlides ? 'fade' : 'slide',
        fadeEffect: {
          crossFade: true
        },
        speed: 600,
        allowTouchMove: hasMultipleSlides,
        navigation: hasMultipleSlides ? {
          nextEl: this.$refs.nextEl,
          prevEl: this.$refs.prevEl
        } : false,
        pagination: hasMultipleSlides ? {
          el: this.$refs.paginationEl,
          clickable: true
        } : false,
        autoplay: hasMultipleSlides ? {
          delay: 5500,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        } : false
      })
    },
    destroySwiper() {
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true)
        this.swiperInstance = null
      }
    },
    handleBannerClick(banner) {
      // Track click
      this.trackClick(banner.id)
      
      // Emit event for parent component
      this.$emit('banner-click', banner)
    },
    async trackView(bannerId) {
      try {
        await bannerAPI.trackView(bannerId)
      } catch (error) {
        // Silent fail - tracking shouldn't interrupt user experience
        console.debug('Banner view tracking failed:', error)
      }
    },
    async trackClick(bannerId) {
      try {
        await bannerAPI.trackClick(bannerId)
      } catch (error) {
        // Silent fail
        console.debug('Banner click tracking failed:', error)
      }
    },
    getBannerTitle(banner) {
      return this.hasImageFallback(banner) ? this.fallbackTitle : this.getDisplayText(banner.title)
    },
    getBannerDescription(banner) {
      return this.hasImageFallback(banner) ? this.fallbackDescription : this.getDisplayText(banner.description)
    },
    getBannerImage(banner) {
      return banner.image_url || this.fallbackImage
    },
    getBannerAlt(banner) {
      return this.getBannerTitle(banner) || 'Banner'
    },
    getDisplayText(value) {
      const text = (value || '').trim()

      if (!text || this.isInternalSliderText(text)) {
        return ''
      }

      return text
    },
    isInternalSliderText(text) {
      if (this.type !== 'main_slider') {
        return false
      }

      const normalizedText = text.replace(/ي/g, 'ی').replace(/ك/g, 'ک')

      return normalizedText === 'اسلایدر' ||
        normalizedText.startsWith('اسلایدر ') ||
        normalizedText.includes('اسلایدر پیشفرض') ||
        normalizedText.includes('اسلایدر پیش‌فرض')
    },
    hasImageFallback(banner) {
      return !banner.image_url || this.failedBannerImageIds.includes(banner.id)
    },
    handleImageError(event, banner) {
      if (banner && !this.hasImageFallback(banner)) {
        this.failedBannerImageIds = [...this.failedBannerImageIds, banner.id]
      }
    }
  }
}
</script>

<style scoped>
.spinner-border {
  width: 2rem;
  height: 2rem;
}

.banner-fade-slider {
  overflow: hidden;
}

.banner-fade-slider :deep(.swiper-slide) {
  transform: scale(0.96);
  transition: opacity 0.6s ease, transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.banner-fade-slider :deep(.swiper-slide-active) {
  transform: scale(1);
}

.banner-fade-slider :deep(.slider-img) {
  min-height: 160px;
  aspect-ratio: 400 / 140;
}

.banner-fade-slider :deep(.slider-img img) {
  transition: transform 0.6s ease;
}

.banner-fade-slider :deep(.swiper-slide-active .slider-img img) {
  transform: scale(1.03);
}

.banner-fade-slider :deep(.sliderSwiper__pagination) {
  bottom: 8px;
}

.slider-img--placeholder {
  background: linear-gradient(135deg, #153a91 0%, #234feb 55%, #72d3ff 100%);
}

.banner-placeholder {
  width: 100%;
  min-height: 160px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  padding: 22px 26px;
  color: #fff;
}

.banner-placeholder__badge,
.banner-placeholder__note {
  width: max-content;
  max-width: 100%;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  padding: 5px 10px;
  font-size: 11px;
  font-family: "IRANSansXFaNum-Medium";
}

.banner-placeholder h3 {
  margin: 0;
  color: #fff;
  font-size: 20px;
  font-family: "IRANSansXFaNum-Medium";
}

.banner-placeholder p {
  margin: 0;
  color: rgba(255, 255, 255, 0.88);
  font-size: 14px;
  line-height: 1.8;
}

.banner-placeholder__note {
  margin-top: 6px;
  background: rgba(18, 35, 76, 0.32);
}

@media (max-width: 480px) {
  .banner-fade-slider :deep(.slider-img) {
    min-height: 150px;
  }

  .banner-placeholder {
    min-height: 150px;
    padding: 18px;
  }

  .banner-placeholder h3 {
    font-size: 18px;
  }
}
</style>


